import { tagName } from "@ember-decorators/component"
import Component from "@ember/component"
import { action, computed, get } from "@ember/object"
import { inject as service } from "@ember/service"
import type { CardStack, CardStackSummary } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { CourseDateCombined } from "@shared/models/course-date-combined"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import findMany from "cc-frontend/helpers/find-many"
import hasManyCollectively from "cc-frontend/helpers/has-many-collectively"
import type { PromiseObject } from "cc-frontend/lib/promise-object"
import type EmberStore from "cc-frontend/services/ember-store"
import type LessonEditor from "cc-frontend/services/lesson-editor"
import type { ModelsNeededForFirstEdit } from "cc-frontend/services/lesson-editor"
import type ModalManager from "cc-frontend/services/modal-manager"
import { extractCardStackTemplateIds } from "cc-frontend/utils/filter-dates/course"
import { toDown, toLeft, toRight, toUp } from "ember-animated/transitions/move-over"
import _ from "lodash"
import { compact, concat, flatMap, map, uniq } from "lodash-es"

let headerNameStack: Array<string> = []
let bodyNameStack: Array<string> = []

@tagName("")
export default class LessonMenu extends Component {
  // args
  modelsNeededForFirstEdit!: ModelsNeededForFirstEdit
  editCourseTitleAndOrder!: Function
  closeMenu!: Function
  courseDate!: CourseDateCombined
  planbook!: Planbook
  rotationCalendar!: RotationCalendar
  course!: Course
  lesson!: CardStack
  dateString!: string

  @service modalManager!: ModalManager
  @service lessonEditor!: LessonEditor
  @service("ember-store") store!: EmberStore

  menuName = "PRIMARY"

  headerNameStack = ["planner/menus/lesson-menu/primary/title"]
  bodyNameStack = ["planner/menus/lesson-menu/primary/body"]

  headerAnimationRules({ oldItems, newItems }) {
    let newVal = newItems[0]
    let oldVal = oldItems[0]
    if (oldVal === undefined || oldVal === null) return toUp
    if (newVal === undefined || newVal === null) return toUp
    let newValInner = Object.getOwnPropertySymbols(newVal).find(
      (s) => s.toString() === "Symbol(INNER)",
    )
    let oldValInner = Object.getOwnPropertySymbols(oldVal).find(
      (s) => s.toString() === "Symbol(INNER)",
    )
    if (newValInner === undefined || oldValInner === undefined)
      throw Error("The inner name might have changed")
    let newComponentName = newVal[newValInner].name
    let oldComponentName = oldVal[oldValInner].name
    // we're going backwards
    if (
      _.includes(headerNameStack, newComponentName) &&
      _.includes(headerNameStack, oldComponentName)
    ) {
      _.pull(headerNameStack, oldComponentName)
      return toDown
    } else {
      headerNameStack.push(newComponentName)
      return toUp
    }
  }

  animationRules({ oldItems, newItems }) {
    let newVal = newItems[0]
    let oldVal = oldItems[0]
    if (oldVal === undefined || oldVal === null) return toLeft
    if (newVal === undefined || newVal === null) return toLeft
    let newValInner = Object.getOwnPropertySymbols(newVal).find(
      (s) => s.toString() === "Symbol(INNER)",
    )
    let oldValInner = Object.getOwnPropertySymbols(oldVal).find(
      (s) => s.toString() === "Symbol(INNER)",
    )
    if (newValInner === undefined || oldValInner === undefined)
      throw Error("The inner name might have changed")
    let newComponentName = newVal[newValInner].name
    let oldComponentName = oldVal[oldValInner].name

    // For the first check, there's an odd case where the new is null and then the new is present and the old is null
    // and this just makes sure we're always going the right way if we're returning home.
    if (
      newComponentName === "planner/menus/lesson-menu/primary/body" ||
      (_.includes(bodyNameStack, newComponentName) && _.includes(bodyNameStack, oldComponentName))
    ) {
      // we're going backwards
      _.pull(bodyNameStack, oldComponentName)
      return toRight
    } else {
      bodyNameStack.push(newComponentName)
      bodyNameStack = _.uniq(bodyNameStack)
      return toLeft
    }
  }

  @hasManyCollectively("planbook", "course", "courses") courses
  @findMany("templateIds", "card-stack-summary") usedTemplates!: PromiseObject<
    Array<CardStackSummary>
  >

  @computed("planbook", "courses")
  get templateIds() {
    let planbookTemplateIds = map(
      get(this.planbook.relationships.cardStackTemplates, "data"),
      (datum) => get(datum, "id"),
    )
    let courseTemplateIds = uniq(
      compact(flatMap(this.courses.content, (course) => extractCardStackTemplateIds(course))),
    )
    return compact(uniq(concat(planbookTemplateIds, courseTemplateIds)))
  }

  @action
  backToPrimaryMenu() {
    this.set("menuName", "PRIMARY")
  }

  @action
  goToChangeTime() {
    this.set("menuName", "CHANGE_TIME")
  }

  @action
  goToNoClass() {
    this.set("menuName", "ADD_NO_CLASS")
  }

  @action
  goToShareLink() {
    this.set("menuName", "SHARE_LINK")
  }

  @action
  goToReplaceTemplate() {
    this.set("menuName", "REPLACE_TEMPLATE")
  }

  @action
  goToTemplateGallery() {
    this.closeMenu()
    this.modalManager.openModal("planner/modals/template-library", {
      planbook: this.planbook,
      rotationCalendar: this.rotationCalendar,
      course: this.course,
      cardStack: this.lesson,
      modelsNeededForFirstEdit: this.modelsNeededForFirstEdit,
    })
  }

  @action
  assignTemplateToDay(templateId) {
    this.store.find("card-stack", templateId).then((cardStack: CardStack) => {
      this.lessonEditor.pasteCardStack(
        this.modelsNeededForFirstEdit,
        this.lesson.id,
        this.course,
        this.planbook,
        cardStack.attributes,
        this.lesson.attributes,
        "template",
        {
          id: cardStack.id,
          version: cardStack.attributes.version,
          courseId: null,
          planbookId: cardStack.relationships.planbook.data.id,
          // I think this is right -- we should really get the ownerIds from the planbook that's referenced in the card stack
          ownerIds: [this.planbook.relationships.owner.data.id],
          source: "template",
        },
      )
      this.closeMenu()
    })
  }
}
